import React, { useMemo, useState } from "react";
import { getColumnCell } from "@common/utils/table.util";
import { ClassroomBooking } from "@models/ISchema";
import styles from "./bookingList.module.scss";
import { Icon } from "@foris/avocado-icons";

interface BookingDatesCellProps {
  booking: ClassroomBooking;
}

const formattedDate = (dateTime: string) => {
  const [date] = dateTime.split(" ");
  const [year, month, day] = date.split("-");
  return `${day}.${month}.${year}`;
};

const BookingDatesCell = ({ booking }: BookingDatesCellProps) => {
  const [isCollapsed, setIsCollapsed] = useState(true);

  const sessionsOrderedByEvent = useMemo(() => {
    return booking?.sessions?.sort((a, b) => {
      if (!a?.scheduleEvent?.length || !b?.scheduleEvent?.length) return 0;

      const dateA = new Date(a.scheduleEvent?.[0]?.start);
      const dateB = new Date(b.scheduleEvent?.[0]?.start);

      return dateA.getTime() - dateB.getTime();
    });
  }, [booking]);

  const totalEventsToShow = useMemo(() => {
    return sessionsOrderedByEvent?.reduce((acc, session) => {
      if (session?.scheduleEvent?.length) {
        return acc + session.scheduleEvent.length;
      }
      return acc;
    }, 0);
  }, [sessionsOrderedByEvent]);

  const firstAndLastSessionLabel = useMemo(() => {
    const firstSessionWithEvent = sessionsOrderedByEvent?.find(
      session => session?.scheduleEvent?.length,
    );
    const lastSessionWithEvent = sessionsOrderedByEvent
      ?.reverse()
      ?.find(session => session?.scheduleEvent?.length);

    const firstStartDate = firstSessionWithEvent?.scheduleEvent[0]?.start;
    const lastEndDate =
      lastSessionWithEvent?.scheduleEvent[lastSessionWithEvent?.scheduleEvent?.length - 1]?.end;

    if (!firstStartDate || !lastEndDate) return "";

    const formattedStartDate = formattedDate(firstStartDate);
    const formattedEndDate = formattedDate(lastEndDate);

    return getColumnCell(
      `${formattedStartDate}${
        formattedEndDate !== formattedStartDate ? ` - ${formattedEndDate}` : ""
      }`,
    );
  }, [sessionsOrderedByEvent]);

  return (
    <div className={styles.bookingDatesCell}>
      {isCollapsed ? (
        firstAndLastSessionLabel
      ) : (
        <ul>
          {sessionsOrderedByEvent?.map((session, index) =>
            session.scheduleEvent.map((event, index2) => {
              const [date] = event.start.split(" ");

              return <li key={index + index2}>{getColumnCell(formattedDate(date))}</li>;
            }),
          )}
        </ul>
      )}

      {totalEventsToShow > 1 && (
        <button
          className={styles.bookingDatesCell_toggle}
          onClick={() => setIsCollapsed(!isCollapsed)}
        >
          <Icon name={isCollapsed ? "chevron-down" : "chevron-up"} />
        </button>
      )}
    </div>
  );
};

export default BookingDatesCell;
